import { useGeneral } from '@utils/general';
import { getWordSearchData } from './word-search-data';

type WordSearchDialogContent = {
  title: string;
  description: string;
};

type WordSearchDialogApi = {
  close: () => void;
  isOpenFor: (anchor: HTMLElement | null) => boolean;
  open: (anchor: HTMLElement, content: WordSearchDialogContent) => void;
  toggle: (anchor: HTMLElement, content: WordSearchDialogContent) => void;
  updatePosition: () => void;
};

const dialogClass = 'word-search-dialog';
const dialogVisibleClass = 'word-search-dialog--visible';
const dialogMobileClass = 'word-search-dialog--mobile';
const dialogMargin = 40;
const dialogOffset = 12;
const dialogArrowSize = 16;
const dialogArrowEdgePadding = 24;
const mobileBreakpoint = 768;

export const useWordSearchDialog = (): WordSearchDialogApi => {
  const { focusTrap } = useGeneral();
  const { closeLabel } = getWordSearchData();
  const dialogElement = document.createElement('div');
  const contentElement = document.createElement('div');
  const headerElement = document.createElement('div');
  const titleElement = document.createElement('p');
  const closeButtonElement = document.createElement('button');
  const descriptionElement = document.createElement('div');
  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
    if (activeAnchor) {
      updatePosition();
    }
  });
  let activeAnchor: HTMLElement | null = null;
  
  // Request animation frame id
  let rafId = 0;

  dialogElement.className = dialogClass;
  dialogElement.setAttribute('role', 'dialog');
  dialogElement.setAttribute('aria-hidden', 'true');

  contentElement.className = `${dialogClass}__content`;
  contentElement.setAttribute('tabindex', '-1');
  headerElement.className = `${dialogClass}__header`;
  titleElement.className = `${dialogClass}__title`;
  closeButtonElement.className = `${dialogClass}__close`;
  closeButtonElement.type = 'button';
  closeButtonElement.setAttribute('aria-label', closeLabel);
  closeButtonElement.innerHTML = '<span class="icon icon--cross" aria-hidden="true"></span>';
  descriptionElement.className = `${dialogClass}__description`;

  headerElement.append(titleElement, closeButtonElement);
  contentElement.append(headerElement, descriptionElement);
  dialogElement.append(contentElement);
  document.body.append(dialogElement);
  
  // Update the trigger's aria-expanded state to reflect whether the dialog is open or closed.
  const updateAnchorState = (anchor: HTMLElement | null, isExpanded: boolean): void => {
    if (!anchor) {
      return;
    }

    anchor.setAttribute('aria-expanded', String(isExpanded));
  };

  const applyPosition = (): void => {
    if (!activeAnchor || !dialogElement.classList.contains(dialogVisibleClass)) {
      return;
    }

    // Mobile uses a full-width/modal-like layout, so desktop positioning styles
    // and arrow placement need to be cleared when crossing the breakpoint.
    if (window.innerWidth < mobileBreakpoint) {
      dialogElement.classList.add(dialogMobileClass);
      dialogElement.removeAttribute('data-position');
      dialogElement.style.removeProperty('left');
      dialogElement.style.removeProperty('top');
      dialogElement.style.removeProperty('--word-search-dialog-arrow-left');
      contentElement.style.maxHeight = 'none';
      return;
    }

    // Reset any mobile-specific styles in case the viewport was previously below the breakpoint.
    dialogElement.classList.remove(dialogMobileClass);
    dialogElement.dataset.position = 'bottom';

    const anchorRect = activeAnchor.getBoundingClientRect();
    const dialogRect = dialogElement.getBoundingClientRect();

    // Center the dialog on the trigger, but clamp it so the panel keeps a
    // consistent margin from the viewport edges.
    const maxLeft = window.innerWidth - dialogRect.width - dialogMargin;
    const centeredLeft = anchorRect.left + ((anchorRect.width - dialogRect.width) / 2);
    const left = Math.min(Math.max(centeredLeft, dialogMargin), Math.max(dialogMargin, maxLeft));

    // Prefer showing below the trigger, but flip above when there is not enough
    // room and the available space above can fit the dialog.
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const dialogHeightAndOffset = dialogRect.height + dialogOffset;
    const shouldPlaceAbove = spaceBelow < dialogHeightAndOffset + dialogMargin && spaceAbove > dialogHeightAndOffset;
    const top = shouldPlaceAbove
      ? Math.max(dialogMargin, anchorRect.top - dialogRect.height - dialogOffset)
      : Math.min(window.innerHeight - dialogRect.height - dialogMargin, anchorRect.bottom + dialogOffset);

    dialogElement.dataset.position = shouldPlaceAbove ? 'top' : 'bottom';
    dialogElement.style.left = `${left}px`;
    dialogElement.style.top = `${Math.max(dialogMargin, top)}px`;

    // Keep the arrow visually aligned with the trigger center, while preventing
    // it from being pushed too close to either edge of the dialog.
    dialogElement.style.setProperty('--word-search-dialog-arrow-left',
      `${Math.min(
        Math.max((anchorRect.left + (anchorRect.width / 2)) - left, dialogArrowEdgePadding),
        dialogRect.width - dialogArrowEdgePadding
      )}px`
    );
    
    // Limit the dialog's max height to the viewport height minus margins and arrow size.
    contentElement.style.maxHeight = `calc(100vh - ${(dialogMargin * 2) + dialogArrowSize}px)`;
  };

  const updatePosition = (): void => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }

    // Batch repeated resize/scroll/observer updates into a single frame so
    // position recalculation does not run excessively.
    rafId = window.requestAnimationFrame(() => {
      rafId = 0;
      applyPosition();
    });
  };

  const close = (): void => {
    if (!activeAnchor) {
      return;
    }

    resizeObserver?.unobserve(activeAnchor);
    updateAnchorState(activeAnchor, false);
    activeAnchor.setAttribute('tabindex', '0');
    dialogElement.classList.remove(dialogVisibleClass);
    dialogElement.setAttribute('aria-hidden', 'true');
    activeAnchor = null;
  };

  const open = (anchor: HTMLElement, content: WordSearchDialogContent): void => {
    // If the dialog is already open, unobserve it before updating the active anchor.
    if (activeAnchor && activeAnchor !== anchor) {
      resizeObserver?.unobserve(activeAnchor);
    }

    updateAnchorState(activeAnchor, false);

    activeAnchor = anchor;
    resizeObserver?.observe(anchor);
    activeAnchor.setAttribute('tabindex', '-1');
    titleElement.textContent = content.title;
    descriptionElement.innerHTML = content.description;

    dialogElement.classList.add(dialogVisibleClass);
    dialogElement.setAttribute('aria-hidden', 'false');

    updateAnchorState(anchor, true);
    updatePosition();

    // Focus on the dialog content for screen readers to be able to read
    contentElement.focus();
  };

  // Returns true if the dialog is currently open for the given anchor element.
  const isOpenFor = (anchor: HTMLElement | null): boolean => {
    return Boolean(anchor) && activeAnchor === anchor && dialogElement.classList.contains(dialogVisibleClass);
  };

  const toggle = (anchor: HTMLElement, content: WordSearchDialogContent): void => {
    if (isOpenFor(anchor)) {
      close();
      return;
    }

    open(anchor, content);
  };

  // Clicking outside the dialog should close it, but clicks inside the dialog or on the trigger should not.
  const handleDocumentPointerDown = (event: PointerEvent): void => {
    const target = event.target as Node | null;

    if (!target || !activeAnchor) {
      return;
    }

    if (dialogElement.contains(target) || activeAnchor.contains(target)) {
      return;
    }

    close();
  };

  const handleDocumentKeydown = (event: KeyboardEvent): void => {
    // Restore focus to the trigger after Escape so keyboard users stay in the
    // same interaction context they came from.
    if (!activeAnchor) {
      return;
    }

    // Trap focus within the dialog while Tab is pressed
    if (event.key === 'Tab') {
      const activeElement = document.activeElement;

      if (activeElement === activeAnchor) {
        event.preventDefault();
        focusTrap(event, [activeAnchor, contentElement, closeButtonElement]);
        return;
      }

      if (activeElement === closeButtonElement) {
        event.preventDefault();
        focusTrap(event, [contentElement, closeButtonElement]);
      }
      return;
    }

    if (event.key !== 'Escape') {
      return;
    }

    const anchorToFocus = activeAnchor;
    close();
    anchorToFocus.focus();
  };

  const handleViewportChange = (): void => {
    if (!activeAnchor) {
      return;
    }

    updatePosition();
  };

  // Clicking the close button should not only close the dialog, but also return focus to the trigger
  closeButtonElement.addEventListener('click', () => {
    const anchorToFocus = activeAnchor;
    close();
    anchorToFocus?.focus();
  });

  document.addEventListener('pointerdown', handleDocumentPointerDown, true);
  document.addEventListener('keydown', handleDocumentKeydown);
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);

  return {
    close,
    isOpenFor,
    open,
    toggle,
    updatePosition
  };
};
